import { Box, Button, TextField, Typography, useTheme } from '@mui/material';
import React, { Dispatch, FC, SetStateAction, useContext, useEffect, useState } from 'react';
import ImageUploader from '../ImageUploader/ImageUploader';
import ColorPicker from '../ColorPicker/ColorPicker';
import CustomFontUploader from '../CustomFontUploader/CustomFontUploader';
import { IDesign } from '../../shared/interfaces/IDesign';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../providers/AuthProvider';
import { OpenInNew } from '@mui/icons-material';
import { v4 as uuid } from 'uuid';
import { ISurvey } from '../../shared/interfaces/ISurvey';
import { indexedDbDraftStorage, storeSurveyPreviewDraft } from '../../shared/SurveyPreviewDraft';

interface Props {
  defaultDesign: IDesign;
  onChange: Dispatch<SetStateAction<IDesign>>;
  surveyType: string;
  buildPreviewSurvey: (design: IDesign) => ISurvey;
  savedSurveyId: string;
}

const DesignChanger: FC<Props> = ({
  defaultDesign,
  onChange,
  surveyType,
  buildPreviewSurvey,
  savedSurveyId,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { hasRole } = useContext(AuthContext);
  const [design, setDesign] = useState<IDesign>(defaultDesign);
  const [previewError, setPreviewError] = useState(false);

  const handlePreview = async () => {
    setPreviewError(false);
    const tab = window.open('about:blank', '_blank');
    const key = uuid();
    try {
      await storeSurveyPreviewDraft(indexedDbDraftStorage, key, {
        survey: { ...buildPreviewSurvey(design), id: savedSurveyId || key },
        type: surveyType,
        savedSurveyId,
        fontDropped: false,
        createdAt: Date.now(),
      });
      const url = `/survey-preview/${surveyType}/${key}?preview=true`;
      if (tab) {
        tab.opener = null;
        tab.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener');
      }
    } catch {
      if (tab) tab.close();
      setPreviewError(true);
    }
  };

  const handleLogoChange = () => (logoFile: File) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const base64Logo = event.target?.result as string;

      setDesign((prevDesign) => ({
        ...prevDesign,
        companyLogo: base64Logo,
      }));
    };

    reader.readAsDataURL(logoFile);
  };

  const handleColorPrimary = () => (colorValue: string) => {
    setDesign({
      ...design,
      primaryColor: colorValue,
    });
  };

  const handleColorSecondary = () => (colorValue: string) => {
    setDesign({
      ...design,
      secondaryColor: colorValue,
    });
  };

  const handleFontChange = () => (fontFile: File) => {
    const reader = new FileReader();
    const extension = fontFile.name.split('.').pop()?.toLowerCase() || '';

    reader.onload = (event) => {
      const base64Font = event.target?.result as string;

      setDesign((prevDesign) => ({
        ...prevDesign,
        customFont: base64Font,
        customFontFormat: extension,
      }));
    };

    reader.readAsDataURL(fontFile);
  };

  const handleCustomCssChange = (customCss: string) => {
    setDesign((prevDesign) => ({
      ...prevDesign,
      customCss,
    }));
  };

  useEffect(() => {
    onChange(design);
  }, [design, onChange]);

  return (
    <Box
      sx={{
        display: 'flex',
        my: theme.spacing(1),
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography>{t('logo_label')}</Typography>
        <ImageUploader onChange={handleLogoChange()} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: theme.spacing(1),
          justifyContent: 'space-between',
        }}
      >
        <Typography>{t('primary_color_label')}</Typography>
        <ColorPicker
          defaultValue={design.primaryColor}
          onChange={handleColorPrimary()}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: theme.spacing(1),
          justifyContent: 'space-between',
        }}
      >
        <Typography>{t('secondary_color_label')}</Typography>
        <ColorPicker
          defaultValue={design.secondaryColor}
          onChange={handleColorSecondary()}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: theme.spacing(1),
          justifyContent: 'space-between',
        }}
      >
        <Typography>{t('font')}</Typography>
        <CustomFontUploader onChange={handleFontChange()} />
      </Box>
      {hasRole('can-use-survey-css') && (
        <Box sx={{ mt: theme.spacing(2) }}>
          <TextField
            fullWidth
            multiline
            minRows={6}
            label={t('custom_css')}
            helperText={t('custom_css_help')}
            value={design.customCss ?? ''}
            onChange={(event) => handleCustomCssChange(event.target.value)}
          />
          <Button
            variant="contained"
            startIcon={<OpenInNew />}
            sx={{ mt: theme.spacing(1), alignSelf: 'flex-start' }}
            onClick={handlePreview}
          >
            {t('preview')}
          </Button>
          {previewError && <Typography color="error">{t('survey_preview_failed')}</Typography>}
        </Box>
      )}
    </Box>
  );
};

export default DesignChanger;
